import React, { useContext } from 'react';
import type { RenderFn } from '@atlassian/forge-ui-types';
import PlatformModalDialog, { ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { FullScreenModalDialog } from '@atlaskit/modal-dialog/full-screen';
import { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
import Image from '@atlaskit/image';
import { Inline } from '@atlaskit/primitives/compiled';
import { ModalProvider } from './ModalProvider';
import { useAdaptEventHandler } from '../../utils';
import type { OnCloseHandler } from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
import { RendererContext } from '../../../context';

/**
 * To simplify API surface and maintain consistent behaviour, the following props are excluded:
 * - onStackChange
 * - stackIndex
 * - isBlanketHidden
 * - shouldCloseOnOverlayClick
 * - shouldCloseOnEscapePress
 * - shouldReturnFocus
 * - onCloseComplete - behaviour can be replicated using `useEffect`
 * - onOpenComplete - behaviour can be replicated using `useEffect`
 */

type PlatformModalDialogProps = React.ComponentProps<typeof PlatformModalDialog>;

export type ModalProps = Pick<
	PlatformModalDialogProps,
	| 'autoFocus'
	| 'children'
	| 'height'
	| 'width'
	| 'onClose'
	| 'shouldScrollInViewport'
	| 'label'
	| 'testId'
> & {
	/** Icon URL to display next to the title only if title is supplied. Fallback is no icon. */
	icon?: string;
	/** Title for the modal header. If supplied, we will render a ModalHeader with a ModalTitle for them. Fallback to app name if not supplied for fullscreen modals. */
	title?: string;
};

/**
 * A modal dialog displays content that requires user interaction, in a layer above the page.
 */
export const Modal = (props: Parameters<RenderFn>[0]): React.JSX.Element => {
	const { autoFocus, height, width, onClose, shouldScrollInViewport, label, testId, title, icon } = props
		.forgeDoc.props as ModalProps;
	const adaptEventHandler = useAdaptEventHandler();
	const { properties, forgeEnvironment } = useContext(RendererContext);
	const extensionTitle = properties?.title;

	const onCloseHandler = adaptEventHandler(onClose) as OnCloseHandler;
	
	const children = props.forgeDoc.children.map((c) => (
		<React.Fragment key={c.key}>{props.render(c)}</React.Fragment>
	));

	const renderModalHeader = (title: string, icon?: string) => 
		<ModalHeader>
			{icon ? (
				<Inline space="space.100" alignBlock="center">
					<Image
						src={icon}
						alt=""
						width="24px"
						height="24px"
					/>
					<ModalTitle>{title}</ModalTitle>
				</Inline>
			) : (
				<ModalTitle>{title}</ModalTitle>
			)}
			<IconButton
				appearance="subtle"
				icon={CrossIcon}
				label="Close Modal"
				onClick={onCloseHandler}
			/>
		</ModalHeader>
	
	const isFullscreenSupported = width === 'fullscreen' && forgeEnvironment !== 'PRODUCTION' && fg('forge-ui-kit-fullscreen-modal');
	
	if (width === 'fullscreen' && forgeEnvironment === 'PRODUCTION') {
		// eslint-disable-next-line no-console
		console.warn(
			'Forge fullscreen modal (EAP) is not available for production apps. The modal will be rendered with xlarge width instead.'
		);
	}

	if (isFullscreenSupported) {
		const fullscreenModal = (
			<FullScreenModalDialog
				autoFocus={autoFocus}
				onClose={onCloseHandler}
				label={label}
				testId={testId}
			>
				{renderModalHeader(title ?? extensionTitle, icon)}
				{children}
			</FullScreenModalDialog>
		);

		return <ModalProvider>{fullscreenModal}</ModalProvider>;
	}

	// Fallback to 'x-large' if width is 'fullscreen' but not supported
	const shouldFallbackFromFullscreen = width === 'fullscreen' && !isFullscreenSupported && fg('forge-ui-add-fallback-for-fullscreen-modal-size');
	const modalWidth = shouldFallbackFromFullscreen ? 'x-large' : width;
	
	if (shouldFallbackFromFullscreen) {
		// eslint-disable-next-line no-console
		console.warn(
			'Forge fullscreen modal is in EAP. Please sign up here https://ecosystem.atlassian.net/servicedesk/customer/portal/1040/group/3496/create/18983. The modal will be rendered with xlarge width instead.'
		);
	}

	const regularModal = (
		<PlatformModalDialog
			autoFocus={autoFocus}
			height={height}
			width={modalWidth}
			onClose={onCloseHandler}
			shouldScrollInViewport={shouldScrollInViewport}
			label={label}
			testId={testId}
		>
			{title && fg('forge-ui-kit-modal-title-icon-props') && (
				renderModalHeader(title, icon)
			)}
			{children}
		</PlatformModalDialog>
	);

	return <ModalProvider>{regularModal}</ModalProvider>;
};
